import React from "react";
import { EuiText, EuiTitle } from "@elastic/eui";
import { useAnketaI18n } from "src/internationalization";
import { AutonubilHomepageFooter } from "./footer";
import { ConsentStatus } from "src/cookie";
import {
  Building2,
  Phone,
  Mail,
  BookOpen,
  Receipt,
  AlertCircle,
} from "lucide-react";

interface ImprintProps {
  i18nPrefix?: string;
}

const C = {
  accent: "var(--ak-accent)",
  accentLight: "var(--ak-accent-tint)",
  accentBorder: "var(--ak-accent-border)",
  pageBg: "var(--ak-page-bg)",
  cardBg: "var(--ak-surface)",
  border: "var(--ak-border)",
  borderHover: "var(--ak-accent-glow)",
  textPrimary: "var(--ak-text)",
  textSecondary: "var(--ak-text-secondary)",
  textMuted: "var(--ak-text-muted)",
  sans: "system-ui,-apple-system,sans-serif",
  radius: 12,
};

interface ImprintSectionProps {
  icon: React.ReactNode;
  title: string;
  children: React.ReactNode;
}

const ImprintSection: React.FC<ImprintSectionProps> = ({
  icon,
  title,
  children,
}) => (
  <div
    style={{
      paddingBottom: 28,
      marginBottom: 28,
      borderBottom: `1px solid ${C.border}`,
    }}
  >
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 10,
        marginBottom: 14,
      }}
    >
      <div
        style={{
          width: 34,
          height: 34,
          flexShrink: 0,
          borderRadius: 8,
          background: C.accentLight,
          border: `1px solid ${C.accentBorder}`,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: C.accent,
        }}
      >
        {icon}
      </div>

      <EuiTitle size="xs">
        <h2 style={{ color: C.textPrimary, fontWeight: 700, margin: 0 }}>
          {title}
        </h2>
      </EuiTitle>
    </div>

    <EuiText color="subdued" size="s">
      <div style={{ lineHeight: 1.75, paddingLeft: 44 }}>{children}</div>
    </EuiText>
  </div>
);

export const AutonubilImprint: React.FC<ImprintProps> = ({
  i18nPrefix = "anketa.autonubil.imprint",
}) => {
  const { translate } = useAnketaI18n();

  // From the translations, not literals: the offer documents read the same
  // keys server-side (functions/documentation/parties.ts), so the imprint and
  // every offer's letterhead cannot disagree.
  const email = translate(`${i18nPrefix}.section.contact.email`, {
    defaultMessage: "info@autonubil.de",
  } as any);
  const [emailUser, emailDomain] = email.split("@");
  const phoneNumber = translate(`${i18nPrefix}.section.contact.phoneNumber`, {
    defaultMessage: "+49 (0) 40 226 59 33 0",
  } as any);

  return (
    <div
      style={{ background: C.pageBg, minHeight: "100vh", fontFamily: C.sans }}
    >
      <div
        style={{
          maxWidth: 800,
          margin: "0 auto",
          padding: "72px clamp(24px, 5vw, 48px) 80px",
        }}
      >
        <div style={{ marginBottom: 40 }}>
          <div
            style={{
              fontSize: "0.7rem",
              fontWeight: 700,
              letterSpacing: "0.1em",
              textTransform: "uppercase",
              color: C.accent,
              marginBottom: 10,
            }}
          >
            {translate(`${i18nPrefix}.eyebrow`, {
              defaultMessage: "Legal",
            } as any)}
          </div>
          <EuiTitle size="l">
            <h1
              style={{
                color: C.textPrimary,
                fontWeight: 800,
                letterSpacing: "-0.02em",
                marginBottom: 0,
              }}
            >
              {translate(`${i18nPrefix}.title`)}
            </h1>
          </EuiTitle>
          <div
            style={{
              width: 56,
              height: 3,
              marginTop: 14,
              marginBottom: 16,
              background: `linear-gradient(90deg, ${C.accent} 0%, var(--ak-accent-2) 100%)`,
              borderRadius: 2,
            }}
          />
          <EuiText color="subdued">
            <p style={{ margin: 0, fontSize: "0.97rem", lineHeight: 1.75 }}>
              {translate(`${i18nPrefix}.description`)}
            </p>
          </EuiText>
        </div>

        <div
          style={{
            background: C.cardBg,
            border: `1px solid ${C.border}`,
            borderRadius: C.radius,
            boxShadow: "0 1px 4px var(--ak-shadow)",
            overflow: "hidden",
          }}
        >
          <div
            style={{
              height: 3,
              background: `linear-gradient(90deg, ${C.accent} 0%, var(--ak-accent-2) 100%)`,
            }}
          />

          <div style={{ padding: "clamp(24px, 4vw, 40px)" }}>
            <ImprintSection
              icon={<Building2 size={17} />}
              title={translate(`${i18nPrefix}.section.company.title`)}
            >
              <p style={{ margin: "0 0 4px" }}>
                {translate(`${i18nPrefix}.section.company.name`)}
              </p>
              <p style={{ margin: "0 0 4px" }}>
                {translate(`${i18nPrefix}.section.company.address`)}
              </p>
              <p style={{ margin: 0 }}>
                {translate(`${i18nPrefix}.section.company.representative`)}
              </p>
            </ImprintSection>

            <ImprintSection
              icon={<Phone size={17} />}
              title={translate(`${i18nPrefix}.section.contact.title`)}
            >
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <Phone size={13} color={C.textMuted} />
                  <span>
                    {translate(`${i18nPrefix}.section.contact.phone`)}
                    {phoneNumber}
                  </span>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <Mail size={13} color={C.textMuted} />
                  <span>
                    E-Mail:{" "}
                    <a
                      href={`mailto:${email}`}
                      style={{
                        color: C.accent,
                        textDecoration: "none",
                        fontWeight: 600,
                      }}
                      onMouseEnter={(e) =>
                        (e.currentTarget.style.textDecoration = "underline")
                      }
                      onMouseLeave={(e) =>
                        (e.currentTarget.style.textDecoration = "none")
                      }
                    >
                      {emailUser} [at] {emailDomain}
                    </a>
                  </span>
                </div>
              </div>
            </ImprintSection>

            <ImprintSection
              icon={<BookOpen size={17} />}
              title={translate(`${i18nPrefix}.section.register.title`)}
            >
              <p style={{ margin: "0 0 4px" }}>
                {translate(`${i18nPrefix}.section.register.court`)}
              </p>
              <p style={{ margin: "0 0 4px" }}>
                {translate(`${i18nPrefix}.section.register.number`)}
              </p>
              <p style={{ margin: 0 }}>
                {translate(`${i18nPrefix}.section.register.duns`)}
              </p>
            </ImprintSection>

            <ImprintSection
              icon={<Receipt size={17} />}
              title={translate(`${i18nPrefix}.section.vat.title`)}
            >
              <p style={{ margin: 0 }}>
                {translate(`${i18nPrefix}.section.vat.id`)}
              </p>
            </ImprintSection>

            <div>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 10,
                  marginBottom: 14,
                }}
              >
                <div
                  style={{
                    width: 34,
                    height: 34,
                    flexShrink: 0,
                    borderRadius: 8,
                    background: C.accentLight,
                    border: `1px solid ${C.accentBorder}`,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: C.accent,
                  }}
                >
                  <AlertCircle size={17} />
                </div>
                <EuiTitle size="xs">
                  <h2
                    style={{ color: C.textPrimary, fontWeight: 700, margin: 0 }}
                  >
                    {translate(`${i18nPrefix}.section.disclaimer.title`)}
                  </h2>
                </EuiTitle>
              </div>
              <EuiText color="subdued" size="s">
                <div style={{ lineHeight: 1.75, paddingLeft: 44 }}>
                  <p style={{ margin: 0 }}>
                    {translate(`${i18nPrefix}.section.disclaimer.text`)}
                  </p>
                </div>
              </EuiText>
            </div>
          </div>
        </div>

        <div style={{ marginTop: 32 }}>
          <ConsentStatus />
        </div>
      </div>

      <AutonubilHomepageFooter />
    </div>
  );
};

export default AutonubilImprint;
